// AgentForge V1's own WebSocket connection.
//
// Same URL legacy uses (`wss://${host}/agentforge/api/v1/ws?token=...`)
// but a separate socket — V1 is self-contained and doesn't share the
// singleton from `useMissionControlWS`. Mount this exactly once
// (in the `AgentForge` umbrella) so each WS frame is processed a
// single time.
//
// Reconnect strategy — four layers, defense in depth:
//   1. last_event_id replay (server XRANGEs the durable Redis stream from
//      the last frame the client saw, so events emitted during the
//      disconnect window aren't lost).
//   2. App-level heartbeat: server sends a `server_ping` JSON frame every
//      ~25s; if no frame at all arrives for 50s, we force-close and
//      reconnect immediately. Catches TCP half-open (LB kill, NAT
//      timeout, server crash that left the socket dangling).
//   3. JWT refresh on close-before-open: if a reconnect attempt closes
//      without ever firing `onopen`, the cached `apiv1Auth` is the most
//      likely cause — we refresh once before retrying so we don't loop
//      forever on a dead token.
//   4. Exponential backoff (1s → 30s) for unrelated server failures.
//
// Subscribers receive raw `{type, payload}` frames; `server_ping` is
// filtered out at this layer because it carries no UI signal.

import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchRefreshToken } from '@/lib/auth-interceptor';

// Wire format from AgentForge WS: { type: "<event_type>", payload: {...} }
// Server may attach an `id` to events that come from the durable Redis
// Streams log; we keep it as the resume cursor for the next reconnect.
export interface AgentForgeWSMessage {
  type: string;
  id?: string;
  payload: Record<string, any>;
}

type MessageHandler = (_msg: AgentForgeWSMessage) => void;

const parseFrameMessages = (data: unknown): AgentForgeWSMessage[] => {
  if (typeof data !== 'string') return [];

  // During rolling deploys an older hub may put multiple newline-delimited
  // Redis events into one browser WS frame. Parse each envelope independently
  // so one malformed line cannot discard its valid neighbours.
  return data.split(/\r?\n/).flatMap((line) => {
    const candidate = line.trim();
    if (!candidate) return [];
    try {
      const parsed = JSON.parse(candidate) as Record<string, unknown>;
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return [];
      }
      if (typeof parsed.type !== 'string') return [];
      const payload =
        parsed.payload &&
        typeof parsed.payload === 'object' &&
        !Array.isArray(parsed.payload)
          ? (parsed.payload as Record<string, any>)
          : {};
      return [
        {
          type: parsed.type,
          ...(typeof parsed.id === 'string' ? { id: parsed.id } : {}),
          payload,
        },
      ];
    } catch {
      return [];
    }
  });
};

/** Redis stream ids are `<milliseconds>-<sequence>`. Returns null for legacy
 * opaque ids, which can still be de-duplicated by exact equality. */
const compareStreamIds = (left: string, right: string): number | null => {
  if (left === right) return 0;
  const leftParts = /^(\d+)-(\d+)$/.exec(left);
  const rightParts = /^(\d+)-(\d+)$/.exec(right);
  if (!leftParts || !rightParts) return null;
  const leftTime = Number(leftParts[1]);
  const rightTime = Number(rightParts[1]);
  if (leftTime !== rightTime) return leftTime > rightTime ? 1 : -1;
  const leftSequence = Number(leftParts[2]);
  const rightSequence = Number(rightParts[2]);
  return leftSequence > rightSequence ? 1 : -1;
};

const MAX_RECONNECT_DELAY_MS = 30_000;
const INITIAL_RECONNECT_DELAY_MS = 1_000;
// If no frame (event OR server_ping) arrives within this window the
// socket is considered dead and we force-reconnect. server_ping ticks
// every ~25s, so 50s = two missed pings = real liveness failure rather
// than a transient hiccup.
const STALE_FRAME_THRESHOLD_MS = 50_000;
const STALE_CHECK_INTERVAL_MS = 5_000;
const TOKEN_BROADCAST_CHANNEL = 'token_broadcast_channel';

const claimString = (
  payload: Record<string, unknown>,
  ...keys: string[]
): string | null => {
  for (const key of keys) {
    const value = payload[key];
    if (typeof value === 'string' || typeof value === 'number') {
      return String(value);
    }
  }
  return null;
};

const opaqueTokenScope = (token: string): string => {
  // Never put an opaque bearer token itself into Web Storage. This small
  // deterministic fingerprint only separates login identities; HTTP turn
  // recovery remains canonical if a token rotation changes the fingerprint.
  let hash = 2166136261;
  for (let i = 0; i < token.length; i += 1) {
    hash ^= token.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return `token-${(hash >>> 0).toString(36)}`;
};

const cursorStorageKey = (token: string): string => {
  let workspaceScope = 'workspace-unknown';
  let subjectScope = opaqueTokenScope(token);
  try {
    const segment = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(
      atob(segment.padEnd(Math.ceil(segment.length / 4) * 4, '='))
    ) as Record<string, unknown>;
    workspaceScope =
      claimString(payload, 'workspace_id', 'account_id', 'accountId') ??
      workspaceScope;
    subjectScope =
      claimString(payload, 'user_id', 'userId', 'sub') ?? subjectScope;
  } catch {
    // Opaque tokens use the non-secret fingerprint above, preventing one login
    // from inheriting another user's cursor in a shared browser session.
  }
  return `agentforge:ws-cursor:${window.location.host}:${encodeURIComponent(
    workspaceScope
  )}:${encodeURIComponent(subjectScope)}`;
};

const readCursor = (key: string): string | null => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeCursor = (key: string, value: string): void => {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Storage can be disabled in hardened/private browser contexts. The live
    // in-memory cursor and turn-status reconciliation still recover safely.
  }
};

const installRefreshedToken = (token: string): boolean => {
  let persisted = false;
  try {
    localStorage.setItem('apiv1Auth', token);
    persisted = true;
  } catch {
    // The hook keeps an in-memory copy below when storage is unavailable.
  }

  // Match auth-interceptor's channel contract so other tabs install the token
  // before their next reconnect as well.
  try {
    if (typeof BroadcastChannel !== 'undefined') {
      const channel = new BroadcastChannel(TOKEN_BROADCAST_CHANNEL);
      channel.postMessage({ type: 'TOKEN_UPDATED', token });
      channel.close();
    }
  } catch {
    // BroadcastChannel can be disabled independently of Web Storage.
  }
  return persisted;
};

const buildWsUrl = (token: string, lastEventId: string | null): string => {
  const agentforgeUrl = import.meta.env.VITE_AGENTFORGE_URL;
  const host = agentforgeUrl
    ? new URL(agentforgeUrl).host
    : window.location.host;
  const proto = agentforgeUrl
    ? agentforgeUrl.startsWith('https')
      ? 'wss'
      : 'ws'
    : window.location.protocol === 'https:'
      ? 'wss'
      : 'ws';
  let url = `${proto}://${host}/agentforge/api/v1/ws?token=${token}`;
  if (lastEventId) {
    url += `&last_event_id=${encodeURIComponent(lastEventId)}`;
  }
  return url;
};

export interface UseAgentForgeWSResult {
  connected: boolean;
  reconnecting: boolean;
  /** Monotonically incremented every time the socket completes a
   * reconnect (i.e. `onopen` fires after a prior `onopen` happened in
   * this hook's lifetime). Consumers can watch this to trigger a
   * session-level refetch — "we lost frames during the gap, sync up
   * via HTTP." Stays at 0 for the very first connect. */
  reconnectGeneration: number;
  subscribe: (_handler: MessageHandler) => () => void;
  /** Send a JSON frame to the server. Returns false if the socket is closed. */
  send: (_data: unknown) => boolean;
}

export const useAgentForgeWS = (): UseAgentForgeWSResult => {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const handlersRef = useRef<Set<MessageHandler>>(new Set());
  const reconnectDelayRef = useRef(INITIAL_RECONNECT_DELAY_MS);
  const mountedRef = useRef(true);
  const wasConnectedRef = useRef(false);
  const connectRef = useRef<() => void>();
  const lastEventIdRef = useRef<string | null>(null);
  const cursorStorageKeyRef = useRef<string | null>(null);
  const inMemoryTokenRef = useRef<string | null>(null);
  const tokenRefreshInFlightRef = useRef(false);
  // 0 until the first `onopen` stamps it. The stale-frame watchdog only
  // reads this once readyState === OPEN, by which point onopen has run.
  const lastFrameAtRef = useRef<number>(0);
  // Has refreshToken already been tried for the current bad-token
  // failure cluster? Reset on a successful open.
  const refreshTriedRef = useRef(false);

  const [connected, setConnected] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [reconnectGeneration, setReconnectGeneration] = useState(0);

  const subscribe = useCallback((handler: MessageHandler) => {
    handlersRef.current.add(handler);
    return () => {
      handlersRef.current.delete(handler);
    };
  }, []);

  // Send a JSON frame to the server (client→server). Returns false if the socket
  // isn't open (caller decides whether to retry). Used to push a refreshed Kula
  // token — all token communication stays on the WS.
  const send = useCallback((data: unknown): boolean => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    try {
      ws.send(typeof data === 'string' ? data : JSON.stringify(data));
      return true;
    } catch {
      return false;
    }
  }, []);

  const scheduleReconnect = useCallback(() => {
    if (!mountedRef.current) return;
    const delay = reconnectDelayRef.current;
    reconnectDelayRef.current = Math.min(delay * 2, MAX_RECONNECT_DELAY_MS);
    reconnectTimeoutRef.current = setTimeout(
      () => connectRef.current?.(),
      delay
    );
  }, []);

  const connect = useCallback(() => {
    if (!mountedRef.current) return;
    if (tokenRefreshInFlightRef.current) return;
    if (
      wsRef.current &&
      (wsRef.current.readyState === WebSocket.CONNECTING ||
        wsRef.current.readyState === WebSocket.OPEN)
    ) {
      return;
    }

    let storedToken: string | null = null;
    try {
      storedToken = localStorage.getItem('apiv1Auth');
    } catch {
      // Fall through to the in-memory token installed after a refresh.
    }
    const token = inMemoryTokenRef.current ?? storedToken;
    if (!token) {
      console.warn(
        '[agentforge] no `apiv1Auth` token in localStorage at this origin — WS will not connect. Log in on this origin first.'
      );
      return;
    }

    const storageKey = cursorStorageKey(token);
    if (cursorStorageKeyRef.current !== storageKey) {
      cursorStorageKeyRef.current = storageKey;
      lastEventIdRef.current = readCursor(storageKey);
    }

    const url = buildWsUrl(token, lastEventIdRef.current);
    console.info(
      '[agentforge] connecting WS:',
      url.replace(/token=[^&]+/, 'token=…')
    );
    const ws = new WebSocket(url);
    let sawOpenThisAttempt = false;

    ws.onopen = () => {
      if (wsRef.current !== ws) {
        ws.close();
        return;
      }
      sawOpenThisAttempt = true;
      refreshTriedRef.current = false;
      lastFrameAtRef.current = Date.now();
      const isReconnect = wasConnectedRef.current;
      wasConnectedRef.current = true;
      setConnected(true);
      setReconnecting(false);
      reconnectDelayRef.current = INITIAL_RECONNECT_DELAY_MS;
      if (isReconnect) {
        // Tell consumers to backfill via HTTP — even with stream replay
        // we may have lost the tail past the replay cap, and a full
        // session refetch is cheap and correct.
        setReconnectGeneration((g) => g + 1);
      }
    };

    ws.onclose = () => {
      // A superseded attempt must never clear or schedule work for the socket
      // that replaced it.
      if (wsRef.current !== ws) return;
      setConnected(false);
      if (wasConnectedRef.current) setReconnecting(true);
      wsRef.current = null;
      if (!mountedRef.current) return;

      // Close-without-open is the canonical "token rejected" pattern.
      // Try one refresh before falling into the exponential backoff
      // loop — if the refresh succeeds the next attempt picks up the
      // fresh token from localStorage.
      if (!sawOpenThisAttempt && !refreshTriedRef.current) {
        refreshTriedRef.current = true;
        tokenRefreshInFlightRef.current = true;
        void fetchRefreshToken()
          .then((result) => {
            const refreshedToken = result?.data?.access_token;
            if (!refreshedToken) {
              throw new Error('Token refresh did not return an access token');
            }
            // Install and fan out the token before scheduling the next socket,
            // otherwise connect() simply reuses the JWT that was just rejected.
            const persisted = installRefreshedToken(refreshedToken);
            inMemoryTokenRef.current = persisted ? null : refreshedToken;
            reconnectDelayRef.current = INITIAL_RECONNECT_DELAY_MS;
            reconnectTimeoutRef.current = setTimeout(
              () => connectRef.current?.(),
              0
            );
          })
          .catch(() => {
            // Do not immediately reconnect with the known-expired token.
            scheduleReconnect();
          })
          .finally(() => {
            tokenRefreshInFlightRef.current = false;
          });
        return;
      }

      scheduleReconnect();
    };

    ws.onerror = () => {
      if (wsRef.current !== ws) return;
      ws.close();
    };

    ws.onmessage = (event) => {
      if (wsRef.current !== ws) return;
      lastFrameAtRef.current = Date.now();
      const messages = parseFrameMessages(event.data);
      for (const msg of messages) {
        // Track resume cursor for the next reconnect. Only emitter
        // events carry ids; legacy hub-broadcast frames (trust events,
        // etc.) don't. server_ping has none either — it's pure
        // liveness signal.
        if (msg.type === 'server_ping') continue;

        if (msg.id && lastEventIdRef.current) {
          const ordering = compareStreamIds(msg.id, lastEventIdRef.current);
          // Replay can overlap the last acknowledged event. Also reject an
          // out-of-order older Redis id so the persisted resume cursor never
          // moves backwards.
          if (ordering !== null && ordering <= 0) continue;
        }

        try {
          handlersRef.current.forEach((handler) => handler(msg));
          // Advance the durable cursor only after every synchronous subscriber
          // has processed the event. A remount can then replay an interrupted
          // handler instead of skipping an event that never reached the store.
          if (msg.id) {
            lastEventIdRef.current = msg.id;
            const key = cursorStorageKeyRef.current;
            if (key) writeCursor(key, msg.id);
          }
        } catch {
          // Keep the cursor before this event and retire the transport. If the
          // socket stayed open, a later frame could otherwise advance the
          // cursor past this failed delivery and make it unreplayable.
          try {
            ws.close();
          } catch {
            // The stale-frame watchdog / visibility recovery remains a
            // fallback for browser implementations that reject close().
          }
          break;
        }
      }
    };

    wsRef.current = ws;
  }, [scheduleReconnect]);

  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  useEffect(() => {
    mountedRef.current = true;
    connect();
    return () => {
      mountedRef.current = false;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect]);

  // Foreground reconnect — when the user returns to the tab and the
  // socket is dead, don't wait out the backoff.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        reconnectDelayRef.current = INITIAL_RECONNECT_DELAY_MS;
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }
        connect();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [connect]);

  // Stale-frame watchdog. The protocol-level WS ping/pong is invisible
  // to browser JS, so an idle session with a half-dead socket would
  // never trip `onclose`. We rely on the server's app-level
  // `server_ping` heartbeat: if it stops arriving, the socket is dead.
  useEffect(() => {
    const interval = setInterval(() => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
      const age = Date.now() - lastFrameAtRef.current;
      if (age > STALE_FRAME_THRESHOLD_MS) {
        console.warn(
          `[agentforge] no WS frames for ${Math.round(age / 1000)}s — forcing reconnect`
        );
        try {
          wsRef.current.close();
        } catch {
          // already closed
        }
      }
    }, STALE_CHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  return { connected, reconnecting, reconnectGeneration, subscribe, send };
};
